import axios from "axios";
import { clearAllLocalStorage, setItem, removeKeyStorage, getItemStorage } from "@abel-technologies-as/utils";
import { getEnvVar } from "@/config";
import { v4 as uuidv4 } from "uuid";
import { showNotifications } from "@/shared/utils";
import { ApiUrl } from "./apiUrl";
import { AccessTokenResponse } from "@/modules/authentication";

const secretKey = getEnvVar("secretKey");

const generateIdempotencyKey = () => {
	const currentIdempotencyKey = getItemStorage("idempotencyKey", secretKey);

	if (currentIdempotencyKey) {
		return currentIdempotencyKey;
	}

	const idempotencyKey = uuidv4();

	setItem("idempotencyKey", idempotencyKey, secretKey);
	return idempotencyKey;
};

const TIMEOUT = 5000;

const axiosInstance = (baseURL = ApiUrl.LEGACY_URL) => {
	const api = axios.create({ baseURL });
	api.interceptors.request.use(
		(config) => {
			config.timeout = TIMEOUT;
			const accessToken = localStorage.getItem("accessToken") ?? "";

			if (accessToken) {
				const cleanToken = accessToken.replace(/"/g, "");

				config.headers.Authorization = `Bearer ${cleanToken}`;
			}
			return config;
		},
		(err) => {
			if (err?.data?.message) {
				showNotifications({ title: "Server Error", message: err.data.message, successNotification: false });
			}

			return Promise.reject(new Error(err));
		}
	);

	let isRefreshing = false;

	api.interceptors.response.use(
		(response) => {
			if (response.status === 200 || response.status === 201) {
				removeKeyStorage("idempotencyKey");
			}

			return response;
		},
		async (error) => {
			const originalRequest = error.config;

			if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
				showNotifications({ title: "Request Aborted", message: error.response.message, successNotification: false });
			}

			if (
				error.response?.status === 401 &&
				!isRefreshing &&
				originalRequest.url !== "authentication/management/login"
			) {
				isRefreshing = true;
				try {
					const refreshToken = localStorage.getItem("refreshToken");

					const { data } = await axios.post<AccessTokenResponse>(
						`${ApiUrl.USER_SERVICE_URL}/authentication/token/refresh`,
						{ refreshToken, clientType: "web" }
					);
					localStorage.setItem("accessToken", data.result.accessToken);
					localStorage.setItem("refreshToken", data.result.refreshToken);
					isRefreshing = false;
					api.defaults.headers.common["Authorization"] = `Bearer ${data.result.accessToken}`;

					document.dispatchEvent(new Event("tokenRefreshed"));
					return api(originalRequest);
				} catch (error) {
					showNotifications({
						title: "Request Aborted",
						message: "Can't get refresh token",
						successNotification: false,
					});
					clearAllLocalStorage();
					window.location.href = `/guest`;
				}
			}

			if (error.response?.status === 400) {
				showNotifications({
					title: "Server Error",
					position: "bottom-right",
					message: error.response.data.message,
					successNotification: false,
				});
			}
			return Promise.reject(error);
		}
	);

	return api;
};

export { axiosInstance, generateIdempotencyKey };
